"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import teacherAPI from "../services/teacher/teacher";

const LOCAL_STORAGE_USER_KEY = "user";
const USER_STORAGE_CHANGED_EVENT = "user-storage-changed";
const AUTH_PENDING_KEY = "auth-pending";
const AGENT_ENV_TIMEOUT_MS = 8000;
// const IS_TEACHER_QUERY_DOMAIN = "thaipilacreate.gforcesolution.com";
const IS_TEACHER_QUERY_DOMAIN = "thailand.pilaproject.org";
const PILA_TEACHER_CLASSES_URL = "https://thailand.pilaproject.org/teacher/classes";
const FORCE_DISABLE_TEACHER_GUARD_FOR_DEV = false; // false for production, true for development
const ENABLE_TEACHER_GUARD = !FORCE_DISABLE_TEACHER_GUARD_FOR_DEV;

function parseIsTeacherValue(result: unknown): boolean | undefined {
    const unwrap = (item: unknown): boolean | undefined => {
        if (typeof item === "boolean") {
            return item;
        }

        if (!item || typeof item !== "object") {
            return undefined;
        }

        const record = item as Record<string, unknown>;
        if (typeof record.value === "boolean") {
            return record.value;
        }
        if (typeof record["is-teacher"] === "boolean") {
            return record["is-teacher"];
        }
        if (typeof record.isTeacher === "boolean") {
            return record.isTeacher;
        }

        return undefined;
    };

    if (Array.isArray(result)) {
        for (const item of result) {
            const parsed = unwrap(item);
            if (parsed !== undefined) {
                return parsed;
            }
        }
        return undefined;
    }

    return unwrap(result);
}

async function queryIsTeacherAndLog() {
    const { default: Agent } = await import("@knowlearning/agents");
    const agent = Agent as typeof Agent & {
        query: (name: string, args: unknown[], domain: string) => Promise<unknown>;
    };
    const result = await agent.query("is-teacher", [], IS_TEACHER_QUERY_DOMAIN);
    const isTeacher = parseIsTeacherValue(result);

    console.log("[auth] Agent.query('is-teacher') result:", result);
    console.log("[auth] Agent.query('is-teacher') value:", isTeacher);

    return isTeacher;
}

export type AuthUser = {
    id?: string;
    provider?: string;
    email?: string;
    name?: string;
    picture?: string;
    role?: string;
    token?: string;
    uuid?: string;
};

export type AuthStatus = "checking" | "authenticated" | "unauthenticated";

export type AuthProvider = "google" | "line";

function notifyUserStorageChanged() {
    window.dispatchEvent(new Event(USER_STORAGE_CHANGED_EVENT));
}

function getLocalStorageUser(): AuthUser | null {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as AuthUser;
        if (!parsed || typeof parsed !== "object") return null;

        return {
            id: parsed.id,
            provider: parsed.provider,
            email: parsed.email,
            name: parsed.name,
            picture: parsed.picture,
            role: parsed.role,
            token: parsed.token,
            uuid: parsed.uuid,
        };
    } catch {
        return null;
    }
}

function saveLocalStorageUser(user: AuthUser) {
    const nextValue = JSON.stringify({
        id: user.id,
        provider: user.provider,
        name: user.name,
        picture: user.picture,
        role: user.role,
        token: user.token,
        uuid: user.uuid,
    });

    const currentValue = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
    if (currentValue === nextValue) return;

    localStorage.setItem(LOCAL_STORAGE_USER_KEY, nextValue);
    notifyUserStorageChanged();
}

function removeLocalStorageUser(notify = true) {
    const hasUser = localStorage.getItem(LOCAL_STORAGE_USER_KEY) !== null;
    localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
    if (notify && hasUser) {
        notifyUserStorageChanged();
    }
}

async function getAgentEnvironmentWithTimeout() {
    const { default: Agent } = await import("@knowlearning/agents");

    return Promise.race([
        Agent.environment(),
        new Promise<never>((_, reject) => {
            window.setTimeout(() => {
                reject(new Error("[auth] Agent.environment timeout"));
            }, AGENT_ENV_TIMEOUT_MS);
        }),
    ]);
}

type AgentEnvironmentAuth = {
    user?: string;
    provider?: string;
    info?: {
        name?: string;
        picture?: string;
    };
};

export function useAuth() {
    const [authStatus, setAuthStatus] = useState<AuthStatus>("checking");
    const [user, setUser] = useState<AuthUser | null>(null);
    const [showNotTeacherPopup, setShowNotTeacherPopup] = useState(false);
    const isLoggingOutRef = useRef(false);

    const resolveAuth = useCallback(async () => {
        const localUser = getLocalStorageUser();
        if (localUser) {
            isLoggingOutRef.current = false;
            setShowNotTeacherPopup(false);
            setUser(localUser);
            setAuthStatus("authenticated");
            return;
        }

        if (isLoggingOutRef.current) {
            setShowNotTeacherPopup(false);
            setUser(null);
            setAuthStatus("unauthenticated");
            return;
        }

        const hasPendingAuth = sessionStorage.getItem(AUTH_PENDING_KEY) === "1";
        if (!hasPendingAuth) {
            setShowNotTeacherPopup(false);
            setUser(null);
            setAuthStatus("unauthenticated");
            return;
        }

        setAuthStatus("checking");

        try {
            const env = await getAgentEnvironmentWithTimeout();

            if (env.auth && env.auth.provider !== "anonymous") {
                const auth = env.auth as AgentEnvironmentAuth;
                const mappedUser: AuthUser = {
                    id: auth.user,
                    provider: auth.provider,
                    name: auth.info?.name,
                    picture: auth.info?.picture,
                };

                if (ENABLE_TEACHER_GUARD) {
                    try {
                        const isTeacher = await queryIsTeacherAndLog();
                        if (isTeacher !== true) {
                            removeLocalStorageUser(false);
                            sessionStorage.removeItem(AUTH_PENDING_KEY);
                            setShowNotTeacherPopup(true);
                            setUser(null);
                            setAuthStatus("unauthenticated");
                            return;
                        }
                    } catch (error) {
                        console.error("[auth] is-teacher query failed while resolving auth", error);
                    }
                }

                saveLocalStorageUser(mappedUser);
                void teacherAPI.createTeacherFromLocalStorage();
                sessionStorage.removeItem(AUTH_PENDING_KEY);
                setShowNotTeacherPopup(false);
                setUser(mappedUser);
                setAuthStatus("authenticated");
                return;
            }

            removeLocalStorageUser(false);
            sessionStorage.removeItem(AUTH_PENDING_KEY);
            setUser(null);
            setAuthStatus("unauthenticated");
        } catch {
            removeLocalStorageUser(false);
            sessionStorage.removeItem(AUTH_PENDING_KEY);
            setUser(null);
            setAuthStatus("unauthenticated");
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const runResolve = async () => {
            await resolveAuth();
        };

        const onStorageChange = () => {
            if (!isMounted) return;
            void runResolve();
        };

        void runResolve();

        window.addEventListener("storage", onStorageChange);
        window.addEventListener(USER_STORAGE_CHANGED_EVENT, onStorageChange);

        return () => {
            isMounted = false;
            window.removeEventListener("storage", onStorageChange);
            window.removeEventListener(USER_STORAGE_CHANGED_EVENT, onStorageChange);
        };
    }, [resolveAuth]);

    const login = useCallback(async (provider: AuthProvider = "google") => {
        try {
            const { default: Agent } = await import("@knowlearning/agents");
            setShowNotTeacherPopup(false);
            sessionStorage.setItem(AUTH_PENDING_KEY, "1");
            await Agent.login(provider);
        } catch (error) {
            console.error(`[auth] Login failed (${provider})`, error);
            sessionStorage.removeItem(AUTH_PENDING_KEY);
        }
    }, []);

    const logout = useCallback(async () => {
        isLoggingOutRef.current = true;
        setUser(null);
        setAuthStatus("unauthenticated");

        try {
            const { default: Agent } = await import("@knowlearning/agents");
            if (typeof Agent.logout === "function") {
                Agent.logout();
            }
        } catch (error) {
            console.error("[auth] Logout failed", error);
        } finally {
            removeLocalStorageUser();
            sessionStorage.removeItem(AUTH_PENDING_KEY);
            setShowNotTeacherPopup(false);
            setUser(null);
            setAuthStatus("unauthenticated");
            isLoggingOutRef.current = false;
        }
    }, []);

    return {
        authStatus,
        user,
        showNotTeacherPopup,
        isTeacherGuardEnabled: ENABLE_TEACHER_GUARD,
        teacherClassesUrl: PILA_TEACHER_CLASSES_URL,
        isAuthenticated: authStatus === "authenticated",
        isChecking: authStatus === "checking",
        login,
        logout,
        dismissNotTeacherPopup: () => setShowNotTeacherPopup(false),
        refreshAuth: resolveAuth,
    };
}
